import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { executeQuery } from "../../../graphql/client";

// Season standings (epic #307 phase 2). A season is an 8-week run of
// tourney nights; this page adds up the per-event compete boards for
// the nights in it: same counting rule as the board (a game counts
// once your RSVP on that night is approved and the game isn't
// disputed; a win = 3 wins on the board, any game you play = at least
// 1). /seasons opens the newest non-archived season; /seasons/<id>
// opens one directly. Members only (same gate as the compete board).

const SEASON_FIELDS = `
  seasonId name sport startDate endDate status eventIds notes
`;

const LIST_QUERY = `
  query Seasons { seasons { ${SEASON_FIELDS} } }
`;

const STANDINGS_QUERY = `
  query SeasonStandings($seasonId: ID!) {
    seasonStandings(seasonId: $seasonId) {
      season { ${SEASON_FIELDS} }
      nightsTotal
      nightsPlayed
      playersCount
      gamesCounted
      leaderName
      myRank
      standings { rank userSub name nightsPlayed gamesLogged gameWins gameLosses boardWins }
      nights { socialEventId title date rsvpYes approvedPlayers gamesCounted gamesPending gamesDisputed leaderName leaderBoardWins }
      monthlyChampions { month label name boardWins runnerUpName runnerUpBoardWins players }
    }
  }
`;

const card = {
  background: "#fff",
  border: "1px solid #e3e7ef",
  borderRadius: 10,
  padding: 16,
  marginBottom: 16,
};

const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#6a7385",
  fontWeight: 600,
  padding: "6px 6px",
  borderBottom: "1px solid #e6e9f0",
  whiteSpace: "nowrap",
};
const td = { padding: "7px 6px", fontSize: 14, verticalAlign: "top" };
const num = { ...td, textAlign: "right", whiteSpace: "nowrap" };

const pill = (active) => ({
  border: active ? "2px solid #0e7c9b" : "1px solid #d7dbe4",
  background: active ? "#e7f4f3" : "#fff",
  color: "#333",
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  cursor: "pointer",
  marginRight: 8,
  marginBottom: 8,
});

const fmtRange = (s) => {
  if (!s || !s.startDate) return "";
  const f = (iso) => {
    const d = new Date(iso + "T12:00:00");
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  return s.endDate ? `${f(s.startDate)} – ${f(s.endDate)}` : f(s.startDate);
};

const STATUS_LABEL = {
  upcoming: "Starts soon",
  active: "In progress",
  ended: "Final",
  archived: "Archived",
};

const Stat = ({ label, value, sub }) => (
  <div style={{ ...card, marginBottom: 0, flex: "1 1 120px", padding: "10px 12px" }}>
    <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: "#1f2330" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#8a93a3" }}>{sub}</div>}
  </div>
);

const Seasons = () => {
  const { seasonId } = useParams();
  const navigate = useNavigate();

  const [seasons, setSeasons] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = (await executeQuery(LIST_QUERY)).seasons || [];
      setSeasons(list);
      const target = seasonId || (list[0] && list[0].seasonId);
      if (!target) {
        setData(null);
        setError("");
        return;
      }
      const res = await executeQuery(STANDINGS_QUERY, { seasonId: target });
      setData(res.seasonStandings);
      setError("");
    } catch (err) {
      setError(err.message || "Could not load the season");
    } finally {
      setLoading(false);
    }
  }, [seasonId]);

  useEffect(() => {
    load();
  }, [load]);

  const wrap = (children) => (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: 16 }}>
      <button
        type="button"
        onClick={() => navigate("/dashboard")}
        style={{
          background: "none",
          border: "none",
          color: "#2f6fb3",
          fontSize: 14,
          cursor: "pointer",
          padding: 0,
          marginBottom: 10,
        }}
      >
        ← Back to dashboard
      </button>
      {children}
    </div>
  );

  if (loading) {
    return wrap(<p style={{ color: "#5b667a" }}>Loading the season…</p>);
  }

  if (error) {
    return wrap(
      <div style={card}>
        <p style={{ color: "#b02a2a", marginBottom: 12 }}>{error}</p>
        <p style={{ color: "#5b667a", fontSize: 14 }}>
          You need to be logged in as a member to see season standings.
        </p>
        <button
          type="button"
          onClick={() => navigate("/login")}
          style={{
            padding: "9px 18px",
            background: "#1f7a3a",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Log in
        </button>
      </div>
    );
  }

  if (!data) {
    return wrap(
      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>
          <span role="img" aria-label="stadium">🏟️</span> No season yet
        </div>
        <p style={{ fontSize: 14, color: "#5b667a", marginBottom: 0 }}>
          When a season of tourney nights is announced, the standings
          live here. Until then, every night still has its own board —
          RSVP, show up, log your games.
        </p>
      </div>
    );
  }

  const { season } = data;
  const mine = data.myRank
    ? data.standings.find((r) => r.rank === data.myRank)
    : null;

  return wrap(
    <>
      {seasons.length > 1 && (
        <div style={{ marginBottom: 6 }}>
          {seasons.map((s) => (
            <button
              key={s.seasonId}
              type="button"
              style={pill(s.seasonId === season.seasonId)}
              onClick={() => navigate(`/seasons/${s.seasonId}`)}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      <h1 style={{ fontSize: 24, marginBottom: 2 }}>
        <span role="img" aria-label="stadium">🏟️</span> {season.name}
      </h1>
      <div style={{ color: "#5b667a", fontSize: 14, marginBottom: 14 }}>
        {[season.sport, fmtRange(season), STATUS_LABEL[season.status] || season.status]
          .filter(Boolean)
          .join(" · ")}
        {season.notes ? ` · ${season.notes}` : ""}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <Stat
          label="Nights"
          value={`${data.nightsPlayed}/${data.nightsTotal}`}
          sub="played / scheduled"
        />
        <Stat label="Players" value={data.playersCount} sub="with counted games" />
        <Stat label="Games" value={data.gamesCounted} sub="counted so far" />
        <Stat
          label={season.status === "ended" ? "Champion" : "Leader"}
          value={data.leaderName || "—"}
          sub={data.standings.length ? `${data.standings[0].boardWins} wins` : "no games yet"}
        />
      </div>

      {mine && (
        <div
          style={{
            ...card,
            background: "#e7f4f3",
            borderColor: "#bfe0dc",
            fontSize: 14,
          }}
        >
          You're <strong>#{mine.rank}</strong> with <strong>{mine.boardWins}</strong>{" "}
          wins on the board ({mine.gameWins}-{mine.gameLosses} in games,{" "}
          {mine.nightsPlayed} {mine.nightsPlayed === 1 ? "night" : "nights"}).
          {data.standings[0] && mine.rank > 1 && (
            <> {data.standings[0].boardWins - mine.boardWins} behind the leader.</>
          )}
        </div>
      )}

      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Standings</div>
        {data.standings.length === 0 ? (
          <p style={{ fontSize: 14, color: "#5b667a", marginBottom: 0 }}>
            No counted games yet. Games count once a captain approves your
            RSVP on that night.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>Player</th>
                  <th style={{ ...th, textAlign: "right" }}>Wins</th>
                  <th style={{ ...th, textAlign: "right" }}>W-L</th>
                  <th style={{ ...th, textAlign: "right" }}>Nights</th>
                </tr>
              </thead>
              <tbody>
                {data.standings.map((r) => (
                  <tr
                    key={r.userSub}
                    style={
                      mine && r.userSub === mine.userSub
                        ? { background: "#f3faf9" }
                        : undefined
                    }
                  >
                    <td style={num}>
                      {r.rank <= 3 ? (
                        <span role="img" aria-label={`rank ${r.rank}`}>
                          {r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : "🥉"}
                        </span>
                      ) : (
                        r.rank
                      )}
                    </td>
                    <td style={td}>{r.name || "Member"}</td>
                    <td style={{ ...num, fontWeight: 700 }}>{r.boardWins}</td>
                    <td style={num}>
                      {r.gameWins}-{r.gameLosses}
                    </td>
                    <td style={num}>{r.nightsPlayed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ fontSize: 12, color: "#8a93a3", marginTop: 8 }}>
          Win = 3 wins on the board, every game you play = at least 1.
          Pending and disputed games don't count until they're cleared.
        </div>
      </div>

      {data.monthlyChampions.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            <span role="img" aria-label="trophy">🏆</span> Monthly champion
          </div>
          {data.monthlyChampions.map((m) => (
            <div key={m.month} style={{ fontSize: 14, marginBottom: 6 }}>
              <strong>{m.label}:</strong> {m.name || "Member"} — {m.boardWins} wins
              {m.runnerUpName
                ? ` (runner-up ${m.runnerUpName}, ${m.runnerUpBoardWins})`
                : ""}
              <span style={{ color: "#8a93a3" }}> · {m.players} players</span>
            </div>
          ))}
        </div>
      )}

      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Nights</div>
        {data.nights.length === 0 ? (
          <p style={{ fontSize: 14, color: "#5b667a", marginBottom: 0 }}>
            No nights scheduled yet.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Night</th>
                  <th style={{ ...th, textAlign: "right" }}>Going</th>
                  <th style={{ ...th, textAlign: "right" }}>Games</th>
                  <th style={th}>Night's best</th>
                </tr>
              </thead>
              <tbody>
                {data.nights.map((n) => (
                  <tr key={n.socialEventId}>
                    <td style={td}>
                      <button
                        type="button"
                        onClick={() => navigate(`/compete/${n.socialEventId}`)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#2f6fb3",
                          padding: 0,
                          cursor: "pointer",
                          fontSize: 14,
                          textAlign: "left",
                        }}
                      >
                        {n.title || n.socialEventId}
                      </button>
                      <div style={{ fontSize: 12, color: "#8a93a3" }}>{n.date}</div>
                    </td>
                    <td style={num}>{n.rsvpYes}</td>
                    <td style={num}>
                      {n.gamesCounted}
                      {n.gamesPending ? (
                        <span style={{ color: "#8a5a00" }}> +{n.gamesPending} pending</span>
                      ) : null}
                    </td>
                    <td style={td}>
                      {n.leaderName ? `${n.leaderName} (${n.leaderBoardWins})` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default Seasons;
